<script setup lang="ts">
  import { ref, watch, computed } from 'vue'
  import { useDateFormat } from '@vueuse/core'
  import { useAmazonCoversMenuStore } from '@/stores/list-item-menu'
  import { useAmazonCoversStore } from '@/stores/amazon-covers'
  import { useAmazonCoversBarStore } from '@/stores/floating-bars'
  import { useSnackbarStore } from '@/stores/snackbar'
  import { useRouter } from 'vue-router'
  import type { TAmazonCover } from '@/types/amazon-cover'
  import { amazonCoverImagePath } from '@/helpers/coverImage'
  import DesHover from '@/components/DesHover.vue'
  import DesHoverMenu from '@/components/DesHoverMenu.vue'
  import DesButtonIcon from '@/components/button/DesButtonIcon.vue'
  import DesEditName from '@/components/DesEditName.vue'
  import DesCheckbox from '@/components/form/DesCheckbox.vue'

  const props = defineProps<{
    item: TAmazonCover
    disableCheckboxes?: boolean
    templateEditor?: boolean
    picker?: boolean
  }>()

  defineEmits<{
    pick: [cover: TAmazonCover]
  }>()

  const covers = useAmazonCoversStore()
  const bar = useAmazonCoversBarStore()
  const snack = useSnackbarStore()
  const router = useRouter()
  const { open } = useAmazonCoversMenuStore()

  const formatDate = (date: string) => useDateFormat(date, 'MMMM DD, YYYY', { locales: 'en-US' })
  const tile = ref<HTMLElement | null>(null)
  const checked = ref<boolean>(bar.is(props.item))

  const edit = (cover: TAmazonCover) => {
    router.push(`/cover-editor/${cover.id}`)
  }

  const renameCover = async (cover: TAmazonCover, title: string) => {
    const oldName = cover.title
    cover.title = title

    const res = await covers.rename(cover.id, cover.title)
    if (res === false) {
      snack.add('Failed to rename cover', { type: 'danger' })
      cover.title = oldName
    }
  }

  watch(
    () => bar.list.length,
    () => (checked.value = bar.is(props.item)),
  )

  // covers generated before the -thumb companion existed only have the full image
  const thumbMissing = ref<boolean>(false)

  const src = computed(() => {
    const path = thumbMissing.value ? props.item.image_url : props.item.thumb_url

    return location.origin + amazonCoverImagePath(props.item.id, path, !thumbMissing.value)
  })

  function onThumbError() {
    if (!thumbMissing.value) {
      thumbMissing.value = true
    }
  }
</script>

<template>
  <div class="flex flex-col gap-1.5" ref="tile">
    <div
      class="relative h-52 px-4 pt-4 bg-white-gray rounded-xl justify-center items-start inline-flex overflow-hidden cursor-pointer border"
      :class="checked ? 'border-blue-50' : 'border-transparent'"
    >
      <div class="h-44 flex-col justify-center items-center inline-flex">
        <img
          v-if="item.thumbnail"
          class="max-h-full max-w-full w-auto h-auto"
          :src="src"
          @error="onThumbError"
          loading="lazy"
          alt=""
          width="0"
          height="0"
        />
        <img
          v-else
          v-once
          class="rounded-md max-h-full max-w-full w-auto h-auto shadow-sm"
          :src="'/assets/images/amazon-cover.png'"
          loading="lazy"
          alt=""
          width="0"
          height="0"
        />
      </div>

      <DesHover v-if="picker" @click.stop="$emit('pick', item)"></DesHover>
      <DesHover v-else data-sortable-handler @click.stop="edit(item)">
        <DesHoverMenu>
          <DesButtonIcon @click.stop="tile && open(item, tile)" icon="fa-light fa-ellipsis" />
        </DesHoverMenu>
        <DesCheckbox v-model="checked" @click.stop.prevent="bar.toggle(item)" v-if="props.disableCheckboxes !== true" />
      </DesHover>
      <DesHover @click.stop.prevent="bar.toggle(item)" v-if="bar.list.length > 0" locked>
        <DesCheckbox v-model="checked" v-if="props.disableCheckboxes !== true" />
      </DesHover>
    </div>
    <div>
      <div class="py-1.5 font-medium text-black-10 text-ellipsis overflow-hidden" v-if="picker">
        {{ item.title }}
      </div>
      <DesEditName v-else :name="item.title" @edit="(newValue: string) => renameCover(item, newValue)" />
      <div class="mt-1 text-black-40 text-xs leading-none">
        {{ item.updated_at ? formatDate(item.updated_at).value : '' }}
      </div>
    </div>
  </div>
</template>
